import React, { useState, useEffect } from 'react';
import { MessageSquare, X, Upload, Loader2 } from 'lucide-react';

interface FeedbackButtonProps {
  onOpen?: () => void;
}

export function FeedbackButton({ onOpen }: FeedbackButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    category: 'bug',
    message: '',
    screenshot: null as File | null,
    website: '' // honeypot — real users never see or fill this
  });
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Only render on client side to avoid SSR issues
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Let any element on the page open this modal by dispatching a window event
  // (e.g. the dashboard "Share feedback" banner). Keeps the single modal
  // instance mounted in root.tsx as the one source of truth.
  useEffect(() => {
    const open = () => { setIsOpen(true); setError(null); setSuccess(false); };
    window.addEventListener('open-feedback', open);
    return () => window.removeEventListener('open-feedback', open);
  }, []);

  // Auto-close modal after successful submission
  useEffect(() => {
    if (success && !isSubmitting) {
      const timer = setTimeout(() => {
        setIsOpen(false);
        setFormData({ email: '', category: 'bug', message: '', screenshot: null, website: '' });
        setPreview(null);
        setError(null);
        setSuccess(false);
      }, 3000); // Close after 3 seconds

      return () => clearTimeout(timer);
    }
  }, [success, isSubmitting]);

  const handleOpen = () => {
    setIsOpen(true);
    setError(null);
    setSuccess(false);
    if (onOpen) onOpen();
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setIsOpen(false);
      setFormData({ email: '', category: 'bug', message: '', screenshot: null, website: '' });
      setPreview(null);
      setError(null);
      setSuccess(false);
    }
  };

  const handleScreenshotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Screenshot must be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file');
        return;
      }
      setFormData({ ...formData, screenshot: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  const handleRemoveScreenshot = () => {
    setFormData({ ...formData, screenshot: null });
    setPreview(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Screenshot must be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file');
        return;
      }
      setFormData({ ...formData, screenshot: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  const getBrowserName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Chrome")) return "Chrome";
    if (ua.includes("Firefox")) return "Firefox";
    if (ua.includes("Safari")) return "Safari";
    if (ua.includes("Edge")) return "Edge";
    if (ua.includes("MSIE") || ua.includes("Trident")) return "IE";
    return "Unknown";
  };

  const getOSName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Win")) return "Windows";
    if (ua.includes("Mac")) return "macOS";
    if (ua.includes("Linux")) return "Linux";
    if (ua.includes("Android")) return "Android";
    if (ua.includes("like Mac")) return "iOS";
    return "Unknown";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    const trimmedEmail = formData.email.trim();
    if (!trimmedEmail) {
      setError('Please provide your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!formData.message.trim()) {
      setError('Please provide feedback message');
      return;
    }

    setIsSubmitting(true);

    try {
      const submitFormData = new FormData();
      submitFormData.append('email', trimmedEmail);
      submitFormData.append('category', formData.category);
      submitFormData.append('message', formData.message.trim());
      submitFormData.append('pageUrl', typeof window !== 'undefined' ? window.location.href : '');
      submitFormData.append('website', formData.website);

      // Capture Technical Context
      if (typeof window !== 'undefined') {
        const metadata = {
          userAgent: navigator.userAgent,
          language: navigator.language,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          screen: `${window.screen.width}x${window.screen.height}`,
          device: window.innerWidth < 768 ? 'Mobile' : 'Desktop',
          platform: navigator.platform,
          // Basic Browser Detection (simple check)
          browser: getBrowserName(),
          os: getOSName()
        };
        submitFormData.append('metadata', JSON.stringify(metadata));
      }
      
      if (formData.screenshot) {
        submitFormData.append('screenshot', formData.screenshot);
      }

      const res = await fetch('/api/feedback', {
        method: 'POST',
        body: submitFormData, // fetch handles multipart/form-data boundary
        // Don't set Content-Type header - browser will set it with boundary for FormData
      });

      if (!res.ok) {
        let errorMessage = 'Failed to submit feedback';
        try {
          const data = await res.json();
          errorMessage = data.message || errorMessage;
        } catch (parseError) {
          // If response is not JSON, try to get text
          try {
            const text = await res.text();
            errorMessage = text || errorMessage;
          } catch (textError) {
            errorMessage = `Server error (${res.status})`;
          }
        }
        throw new Error(errorMessage);
      }

      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isMounted) {
    return null;
  }

  return (
    <>
      {/* Feedback Button - Minimal icon button at bottom right. A page with a
          sticky bottom bar (the homepage's mobile CTA) sets --fab-bottom so
          the bubble floats above the bar instead of covering its right end. */}
      <div className="fixed right-4 z-50" style={{ bottom: 'var(--fab-bottom, 1rem)' }}>
        <button
          className="bg-pink-500 text-white rounded-full p-3 shadow-lg hover:bg-pink-600 hover:scale-110 transition-all duration-200 group"
          onClick={handleOpen}
          aria-label="Submit Feedback"
          title="Send Feedback"
        >
          <MessageSquare size={20} className="rotate-180" />
        </button>
      </div>

      {/* Feedback Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4" onClick={handleClose}>
          {/* Cap by dvh, not vh: mobile Safari/Chrome overlay their toolbars on
              top of the vh viewport, which pushed the Submit button off-screen
              behind the bottom bar. Header is pinned; only the body scrolls, so
              the CTA is always reachable. */}
          <div className="bg-white rounded-xl w-full max-w-xl max-h-[90dvh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <h2 className="text-2xl font-bold text-gray-900">Submit Feedback</h2>
              <button
                className="p-2 hover:bg-gray-100 rounded transition-colors"
                onClick={handleClose}
                disabled={isSubmitting}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {error && <div className="bg-red-100 text-red-800 p-3 rounded-lg mb-5 text-sm">{error}</div>}
              {success && (
                <div className="bg-green-100 text-green-800 p-3 rounded-lg mb-5 text-sm">
                  Thank you for your feedback! We'll review it soon.
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}
                />
                <div className="mb-5">
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Email Address *</label>
                  <input
                    type="email"
                    className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-500/20 transition-all"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    onBlur={(e) => {
                      const trimmed = e.target.value.trim();
                      if (trimmed !== formData.email) {
                        setFormData({ ...formData, email: trimmed });
                      }
                    }}
                    placeholder="your.email@example.com"
                    required
                    aria-required="true"
                    disabled={isSubmitting}
                    autoComplete="email"
                  />
                </div>

                <div className="mb-5">
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Category *</label>
                  <select
                    className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-500/20 bg-white"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                    aria-required="true"
                    disabled={isSubmitting}
                  >
                    <option value="bug">Bug Report</option>
                    <option value="feature">Feature Request</option>
                    <option value="improvement">Improvement Suggestion</option>
                    <option value="question">Question</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="mb-5">
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Message *</label>
                  <textarea
                    className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-500/20 min-h-[120px]"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Please describe your feedback in detail..."
                    required
                    aria-required="true"
                    disabled={isSubmitting}
                  />
                </div>

                <div className="mb-5">
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Screenshot (Optional)</label>
                  {!preview ? (
                    <label
                      className={`block border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
                        dragOver ? 'border-pink-500 bg-pink-50' : 'border-gray-300 bg-gray-50 hover:border-pink-500 hover:bg-pink-50'
                      }`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleScreenshotChange}
                        disabled={isSubmitting}
                      />
                      <div className="flex flex-col items-center gap-2">
                        <Upload size={24} className="text-gray-500" />
                        <div className="text-sm text-gray-500">
                          Click to upload or drag and drop
                        </div>
                        <div className="text-xs text-gray-400">
                          PNG, JPG, GIF up to 5MB
                        </div>
                      </div>
                    </label>
                  ) : (
                    <div className="mt-3 relative inline-block">
                      <img src={preview} alt="Screenshot preview" className="max-w-full max-h-[200px] rounded-lg border border-gray-300" />
                      <button
                        type="button"
                        className="absolute -top-2 -right-2 bg-pink-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-pink-600 transition-colors shadow-sm"
                        onClick={handleRemoveScreenshot}
                        disabled={isSubmitting}
                        aria-label="Remove screenshot"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full py-3 px-6 bg-pink-500 text-white rounded-lg font-semibold text-base cursor-pointer hover:bg-pink-600 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Feedback'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
